import type { IUsers } from "@ports/auth/IUsers";
import { type Result, ok, err } from "@domain/Result";

type ValidateTokenError = "invalid_token" | "expired_token";
type SetPasswordError = "token_no_longer_valid";

interface PasswordResetUseCasesDeps {
	userApi: IUsers;
}

/**
 * Three-step password reset flow:
 *  1. `requestReset(email)` — emails a reset link.
 *  2. `validateToken(token)` — when the user follows the link, we confirm
 *     the token is good and recover the email it was issued to.
 *  3. `setNewPassword(email, password, token)` — commits the new password.
 *
 * Bundled together because they share the `IUsers` dependency and form a
 * single conceptual flow.
 */
export interface PasswordResetUseCases {
	requestReset(email: string): Promise<void>;
	validateToken(token: string): Promise<Result<{ email: string }, ValidateTokenError>>;
	setNewPassword(email: string, password: string, token: string): Promise<Result<void, SetPasswordError>>;
}

async function requestReset(deps: PasswordResetUseCasesDeps, email: string): Promise<void> {
	await deps.userApi.requestPasswordReset(email);
}

async function validateToken(
	deps: PasswordResetUseCasesDeps,
	token: string,
): Promise<Result<{ email: string }, ValidateTokenError>> {
	if (!token) return err("invalid_token");
	const email = await deps.userApi.validateResetToken(token);
	if (!email) return err("invalid_token");
	return ok({ email });
}

async function setNewPassword(
	deps: PasswordResetUseCasesDeps,
	email: string,
	password: string,
	token: string,
): Promise<Result<void, SetPasswordError>> {
	const success = await deps.userApi.setNewPassword(email, password, token);
	if (!success) return err("token_no_longer_valid");
	return ok(undefined);
}

export function makePasswordResetUseCases(deps: PasswordResetUseCasesDeps): PasswordResetUseCases {
	return {
		requestReset: (email) => requestReset(deps, email),
		validateToken: (token) => validateToken(deps, token),
		setNewPassword: (email, password, token) => setNewPassword(deps, email, password, token),
	};
}
